import { inject, observer } from "mobx-react";
import { useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Link,
  Divider,
} from "@mui/material";

import "./tutorial.scss";
import BigImage from "../../components/BigImage/BigImage";
import tutor_1 from "../../constants/img/turorial/1.jpg";
import tutor_2 from "../../constants/img/turorial/2.png";
import tutor_3 from "../../constants/img/turorial/3.jpg";
import tutor_4 from "../../constants/img/turorial/4.jpg";
import tutor_5 from "../../constants/img/turorial/5.jpg";
import tutor_6 from "../../constants/img/turorial/6.jpg";
import tutor_7 from "../../constants/img/turorial/7.jpg";
import tutor_8 from "../../constants/img/turorial/8.jpg";

const TutorDetails = [
  {
    title: "Basic information",
    titlesize: "h4",
    picture: "",
    text: "DeepBIO is a user-friendly interpretable deep-learning platform for biological sequence functional analysis, such as sequence-level functional prediction and base-wise functional annotation. As for the sequence-level functional prediction, DeepBIO integrates a total of 42 state-of-the-art deep-learning approaches, and enables a one-stop-shop web service for researchers to develop a new deep learning architecture to answer any biological question. To be specific, given any biological sequence data, DeepBIO allows to train and evaluate deep-learning models, conduct model comparison and optimization, and provide a series of visualization analyses. As for the base-wise functional site annotation, DeepBIO offers well-pretrained deep-learning architectures for over 20 functional site annotation tasks, supports comprehensive interpretations and graphical visualizations of identified sites, and validates the reliability of the identified sites by conservation motif analysis.",
    child: [],
  },
  {
    title: "Main Functions",
    titlesize: "h4",
    picture: "",
    text: "",
    child: [
      {
        title: "Deep learning based prediction",
        titlesize: "h5",
        picture: "",
        text: "",
        child: [
          {
            title: "Input data",
            titlesize: "h6",
            picture: tutor_1,
            text: "DeepBIO serves as an automatically integrated analysis platform based on biological sequence data, including DNA, RNA, and protein, which can be entered in the online input box or uploaded with the standard file format. Users can also choose different training strategies to use in model training, such as cdhit, etc.",
            child: [],
          },
          {
            title: "Model select",
            titlesize: "h6",
            picture: tutor_2,
            text: "There are a total of 42 deep learning models, covering classic deep-learning models (e.g. Deep Neural Networks (DNN), Gate Recurrent Unit (GRU), etc), Natural Language Processing (NLP) models (e.g. Transformer, Bidirectional Encoder Representations from Transformers (BERT), etc), and Graph Neural Network (GNN) models (e.g. Graph Convolutional Network (GCN), etc). Notably, each of the above models can be exploited to deal with all three types (DNA, RNA, and protein) of sequence analysis tasks. It is worth noting that users can only select up to a maximum of six models.",
            child: [],
          },
          {
            title: "Submission",
            titlesize: "h6",
            picture: tutor_3,
            text: "In order to get the model prediction results in time, we recommend users to submit their personal email address in order to get the link to the results, and at the bottom we also provide examples of the results and the function to empty the form.",
            child: [],
          },
        ],
      },

      {
        title: "Sequence functional annotation",
        titlesize: "h5",
        picture: "",
        text: "",
        child: [
          {
            title: "Model selection and task selection",
            titlesize: "h6",
            picture: tutor_4,
            text: "In this step, users can select our default models or the models they have trained in previous tasks. For the default models, we provide several deep-learning pre-trained models, such as DNA-BERT, etc. For the user-trained models, they can choose one model in previously completed tasks. It is noted that if users select models by themselves, the type of analyzed biological sequences and task must be the same as that in the previously completed task. There are several functional annotation tasks for users to select, which are composed of three types: DNA methylation, RNA methylation, and protein-ligand binding. For example, there are DNA N4-methylcytosine (4mC) prediction, DNA 5-methylcytosine (5mc) prediction, and DNA N6-methyladenine (6mA) prediction in DNA methylation task.",
            child: [],
          },
          {
            title: "Data input",
            titlesize: "h6",
            picture: tutor_5,
            text: "In this step, DeepBIO needs users to provide and upload the sequences they want to annotate. Particularly, for the DNA methylation prediction task, we support two ways for users to input sequence data, that is: (1) uploading sequence data by themselves, (2) selecting a segment of our preset human gene by choosing a specific cell line and chromosome.",
            child: [],
          },
          {
            title: "Submission",
            titlesize: "h6",
            picture: tutor_6,
            text: "As above, we have also designed similar functions for submission.",
            child: [],
          },
        ],
      },
    ],
  },

  {
    title: "Result report",
    titlesize: "h4",
    picture: "",
    text: "",
    child: [
      {
        title: "",
        titlesize: "h6",
        picture: tutor_7,
        text: "Users can find the completed task through the link sent by email or the corresponding jobid, and get a visual analysis of the model results.",
        child: [],
      },
      {
        title: "",
        titlesize: "h6",
        picture: tutor_8,
        text: "To make it more convenient for users to save our visualizations, they can access the download links for each image via the button at the top right of each visualization.",
        child: [],
      },
    ],
  },
];

function Tutor(data) {
  let return_list = [
    <Divider>
      <Typography
        variant={data.titlesize}
        component="div"
        className="tur-title-text"
      >
        {data.title}
      </Typography>
    </Divider>,
  ];
  if (data.picture) {
    return_list.push(<BigImage src={data.picture}></BigImage>);
  }
  if (data.text) {
    return_list.push(
      <Typography
        variant="p"
        sx={{ lineHeight: 2, mt: 5 }}
        component="div"
        className="tur-title-text"
      >
        {data.text}
      </Typography>
    );
  }
  if (data.child.length > 0) {
    for (let index in data.child) {
      if (data.child[index].title) {
        if (data.child[index].text) {
          return_list.push(
            <Container sx={{ mt: 5 }}>
              <Divider>
                <Typography
                  variant={data.child[index].titlesize}
                  component="div"
                  className="tur-title-text"
                >
                  {data.child[index].title}
                </Typography>
              </Divider>
              <Typography
                variant="p"
                component="div"
                className="tur-title-text"
                sx={{ lineHeight: 2 }}
              >
                {data.child[index].text}
              </Typography>
            </Container>
          );
        } else {
          return_list.push(
            <Divider sx={{ m: 4 }}>
              <Typography
                variant={data.child[index].titlesize}
                component="div"
                className="tur-title-text"
                sx={{ m: 2 }}
              >
                {data.child[index].title}
              </Typography>
            </Divider>
          );
          let result_list_child = [];
          if (data.child[index].child.length > 0) {
            data.child[index].child.forEach((element) => {
              result_list_child.push(
                <Container sx={{ mb: 10 }}>
                  <Divider>
                    <Typography
                      variant={element.titlesize}
                      component="div"
                      className="tur-title-text"
                      sx={{ m: 2 }}
                    >
                      {element.title}
                    </Typography>
                  </Divider>

                  <BigImage src={element.picture} />
                  <Typography
                    variant="p"
                    component="div"
                    className="tur-title-text"
                    sx={{ lineHeight: 2 }}
                  >
                    {element.text}
                  </Typography>
                  <Divider sx={{ marginBottom: 2 }} />
                  {/* <Divider variant="middle" /> */}
                </Container>
              );
            });
          }

          return_list.push(
            <Card sx={{ minWidth: 300 }}>
              <CardContent sx={{ ml: 2 }}>{result_list_child}</CardContent>
            </Card>
          );
        }
      } else {
        if (data.child[index].text && data.child[index].picture)
          return_list.push(
            <Container sx={{ mb: 10 }}>
              <BigImage src={data.child[index].picture} />
              <Typography
                variant="p"
                component="div"
                className="tur-title-text"
                sx={{ lineHeight: 2 }}
              >
                {data.child[index].text}
              </Typography>
            </Container>
          );
      }
    }
  }
  return (
    // <Container className="ref-body"  maxWidth="xl">
    <Grid container spacing={2}>
      <Grid
        item
        md={12}
        sm={12}
        xs={12}
        container
        direction="column"
        spacing={4}
      >
        <Grid item>
          <CardContent sx={{ ml: 2 }}>
            <Card sx={{ minWidth: 300 }}>
              <CardContent sx={{ ml: 2 }}>
                {return_list}

                <Grid item></Grid>
                <Grid item></Grid>
              </CardContent>
            </Card>
          </CardContent>
        </Grid>
      </Grid>
    </Grid>
    // </Container>
  );
}

//name  link author book doi PDF
function Tutorial(store) {
  useEffect(() => {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }, []);

  const scrollToAnchor = (anchorName) => {
    if (anchorName) {
      // 找到锚点
      let anchorElement = document.getElementById(anchorName);
      // 如果对应id的锚点存在，就跳转到锚点
      if (anchorElement) {
        anchorElement.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    }
  };
  return (
    <Container className="tur-body" maxWidth="xl">
      <Box className="tur-title" color="primary.light">
        <Typography variant="h3" component="div" className="tur-title-text">
          Tutorial
        </Typography>
      </Box>
      {TutorDetails.map((value, index, array) => {
        let list_result = [];
        list_result.push(Tutor(value));
        return list_result;
      })}
      {/* <div id="activity1"></div> */}
    </Container>
  );
}

export default inject("store")(observer(Tutorial));
